import { useRef } from "react"
import { Box, Button, Card, CircularProgress, IconButton, TextField, Typography } from "@mui/material"
import { Upload, Users, X } from "lucide-react"
import {
  type Speaker,
  type WebinarSection,
  SPEAKER_ACCENT_PALETTE,
  toAbsoluteUrl,
  toPreviewImageUrl,
} from "./stock-images"

// Editor UI for the "Meet the Speakers" section. Each speaker has a circular
// photo (clickable to open the crop modal), a name, and a multi-line title.
// The list of speakers is also paired with `speakersToHtml` below, which is
// inlined inside webinar_details in the rendered email — never as a standalone
// section in the email.
interface SpeakersSectionProps {
  section: WebinarSection
  bgColor: string
  uploadingSpeakerId: string | null
  onUpdate: (sectionId: string, partial: any) => void
  onUpdateSpeaker: (sectionId: string, speakerId: string, patch: Partial<Speaker>) => void
  onSpeakerPhotoUpload: (
    sectionId: string,
    speakerId: string,
    event: React.ChangeEvent<HTMLInputElement>
  ) => void
  onOpenCrop: (sectionId: string, speakerId: string) => void
}

const PREVIEW_SIZE = 96

export function SpeakersSection({
  section,
  bgColor,
  uploadingSpeakerId,
  onUpdate,
  onUpdateSpeaker,
  onSpeakerPhotoUpload,
  onOpenCrop,
}: SpeakersSectionProps) {
  // The speaker photo file inputs need stable refs so the "Upload photo" button
  // can `.click()` the matching hidden input. Refs are scoped to this component
  // so they are torn down with it (no shared global ref).
  const speakerPhotoInputRefs = useRef<Record<string, HTMLInputElement | null>>({})

  const speakers: Speaker[] = Array.isArray(section.content.speakers) ? section.content.speakers : []
  const enabled: boolean = section.content.enabled !== false

  return (
    <Card key={section.id} sx={{ borderRadius: 0 }} style={{ backgroundColor: bgColor, marginBottom: 0 }}>
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
          <Typography component="span" sx={{ fontSize: "1.125rem", fontWeight: 600, display: "flex", alignItems: "center", gap: 1 }}>
            <Users size={20} />
            Speakers
          </Typography>
          <Box component="label" sx={{ display: "flex", alignItems: "center", gap: 1, fontSize: 14, color: "text.secondary", cursor: "pointer" }}>
            <Box
              component="input"
              type="checkbox"
              checked={enabled}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => onUpdate(section.id, { enabled: e.target.checked })}
              sx={{ height: 16, width: 16 }}
            />
            Show in email
          </Box>
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Box>
            <Typography component="label" htmlFor={`speakers-heading-${section.id}`} sx={{ fontSize: 14, fontWeight: 500 }}>Section Heading</Typography>
            <TextField
              fullWidth
              size="small"
              id={`speakers-heading-${section.id}`}
              value={section.content.heading || ''}
              onChange={(e) => onUpdate(section.id, { heading: e.target.value })}
              placeholder="Meet the Speakers"
            />
          </Box>

          <Box
            sx={{
              display: "grid",
              gap: 2,
              gridTemplateColumns: {
                md: speakers.length >= 3 ? "repeat(3, 1fr)" : speakers.length === 2 ? "repeat(2, 1fr)" : "1fr",
              },
            }}
          >
            {speakers.map((speaker, index) => {
              const initials = (speaker.name || '?')
                .split(/\s+/).filter(Boolean).slice(0, 2)
                .map(p => p[0]?.toUpperCase() || '').join('') || '?'
              const accent = SPEAKER_ACCENT_PALETTE[index % SPEAKER_ACCENT_PALETTE.length]
              const isUploading = uploadingSpeakerId === speaker.id
              const objectPos = speaker.objectPosition || '50% 50%'

              return (
                <Box
                  key={speaker.id}
                  sx={{ border: 1, borderColor: "divider", borderRadius: 1, p: 2, bgcolor: "background.paper", display: "flex", flexDirection: "column", gap: 1.5 }}
                >
                  <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
                    {speaker.photoUrl ? (
                      <>
                        <Box
                          component="button"
                          type="button"
                          onClick={() => onOpenCrop(section.id, speaker.id)}
                          aria-label={`Adjust crop for ${speaker.name || 'speaker'}`}
                          title="Click to adjust crop"
                          style={{
                            // Uploaded speaker photos come back as a relative
                            // `/api/v1/image/<key>` URL; anchor it to the
                            // dashboard same-origin proxy so the preview renders
                            // on the SPA origin AND the desktop app
                            // (`tauri://localhost`). A raw relative url is the
                            // broken-avatar bug (2026-06-18).
                            backgroundImage: `url(${toPreviewImageUrl(speaker.photoUrl)})`,
                            backgroundPosition: objectPos,
                          }}
                          sx={{
                            position: "relative",
                            width: PREVIEW_SIZE,
                            height: PREVIEW_SIZE,
                            borderRadius: "50%",
                            border: "2px solid",
                            borderColor: "divider",
                            boxShadow: 1,
                            backgroundSize: "cover",
                            backgroundRepeat: "no-repeat",
                            cursor: "pointer",
                            transition: "box-shadow 0.15s",
                            "&:hover": { boxShadow: (t) => `0 0 0 2px ${t.palette.primary.light}` },
                            "&:hover .crop-overlay": { bgcolor: "rgba(0,0,0,0.3)", opacity: 1 },
                          }}
                        >
                          <Box component="span" sx={{ position: "absolute", width: 1, height: 1, overflow: "hidden", clip: "rect(0 0 0 0)" }}>Adjust crop</Box>
                          <Box
                            component="span"
                            className="crop-overlay"
                            sx={{ position: "absolute", inset: 0, borderRadius: "50%", bgcolor: "rgba(0,0,0,0)", transition: "all 0.15s", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 12, fontWeight: 500, opacity: 0 }}
                          >
                            Adjust crop
                          </Box>
                        </Box>
                        <Typography sx={{ fontSize: 11, color: "text.secondary" }}>
                          Click photo to reposition
                        </Typography>
                      </>
                    ) : (
                      <Box
                        sx={{ height: 96, width: 96, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontWeight: 700, fontSize: "1.5rem" }}
                        style={{ backgroundColor: accent }}
                      >
                        {initials}
                      </Box>
                    )}
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontSize: 12, textTransform: "uppercase", letterSpacing: "0.05em", color: "text.secondary" }}>Speaker {index + 1}</Typography>
                        <Typography sx={{ fontSize: 14, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{speaker.name || 'Unnamed speaker'}</Typography>
                      </Box>
                      {speaker.photoUrl && (
                        <IconButton
                          size="small"
                          onClick={() => onUpdateSpeaker(section.id, speaker.id, { photoUrl: '', objectPosition: '50% 50%' })}
                          title="Remove photo"
                        >
                          <X size={16} />
                        </IconButton>
                      )}
                    </Box>
                  </Box>

                  <Box>
                    <Typography component="label" htmlFor={`speaker-name-${speaker.id}`} sx={{ fontSize: 12, fontWeight: 500 }}>Name</Typography>
                    <TextField
                      fullWidth
                      size="small"
                      id={`speaker-name-${speaker.id}`}
                      value={speaker.name}
                      onChange={(e) => onUpdateSpeaker(section.id, speaker.id, { name: e.target.value })}
                      placeholder="Full name"
                    />
                  </Box>

                  <Box>
                    <Typography component="label" htmlFor={`speaker-title-${speaker.id}`} sx={{ fontSize: 12, fontWeight: 500 }}>
                      Title (one line per row)
                    </Typography>
                    <TextField
                      fullWidth
                      multiline
                      size="small"
                      id={`speaker-title-${speaker.id}`}
                      value={speaker.title}
                      onChange={(e) => onUpdateSpeaker(section.id, speaker.id, { title: e.target.value })}
                      placeholder={"Job Title\nCompany / Role"}
                      rows={2}
                    />
                  </Box>

                  <input
                    ref={(el) => { speakerPhotoInputRefs.current[speaker.id] = el }}
                    type="file"
                    accept="image/*"
                    style={{ display: "none" }}
                    onChange={(e) => onSpeakerPhotoUpload(section.id, speaker.id, e)}
                  />
                  <Button
                    type="button"
                    variant="outlined"
                    color="inherit"
                    size="small"
                    fullWidth
                    onClick={() => speakerPhotoInputRefs.current[speaker.id]?.click()}
                    disabled={isUploading}
                    startIcon={isUploading ? <CircularProgress size={16} color="inherit" /> : <Upload size={16} />}
                  >
                    {isUploading ? "Uploading..." : (speaker.photoUrl ? 'Replace photo' : 'Upload photo')}
                  </Button>
                </Box>
              )
            })}
          </Box>

          {!enabled && (
            <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
              Speakers section is hidden — toggle &ldquo;Show in email&rdquo; above to include it.
            </Typography>
          )}
        </Box>
      </Box>
    </Card>
  )
}

// Build the speakers HTML block. This block is *inlined inside webinar_details*
// (between the date/time/duration cards and the About description) — see
// `webinar-details-section.tsx`. Returns an empty string when:
//   * the section is missing
//   * the section is disabled (enabled === false)
//   * there are no speakers with a name or photo
//
// The empty-string contract lets the caller splice the result into HTML
// unconditionally without affecting layout when speakers are off.
export function speakersToHtml(section: WebinarSection | undefined): string {
  if (!section) return ''
  if (section.content.enabled === false) return ''

  const speakerList: Speaker[] = Array.isArray(section.content.speakers)
    ? section.content.speakers
    : []
  const visibleSpeakers = speakerList.filter(s =>
    (s.name && s.name.trim().length > 0) ||
    (s.photoUrl && s.photoUrl.trim().length > 0)
  )
  if (visibleSpeakers.length === 0) return ''

  const speakersHeading = section.content.heading || 'Meet the Speakers'
  const speakersBg = section.backgroundColor || '#f7fafc'

  const renderSpeakerCard = (speaker: Speaker, index: number): string => {
    const initials = (speaker.name || '?')
      .split(/\s+/).filter(Boolean).slice(0, 2)
      .map(p => p[0]?.toUpperCase() || '').join('') || '?'
    const accent = SPEAKER_ACCENT_PALETTE[index % SPEAKER_ACCENT_PALETTE.length]
    const photoUrl = toAbsoluteUrl(speaker.photoUrl)
    const safeName = (speaker.name || 'Speaker').replace(/"/g, '&quot;')

    // Break multi-line titles ("CEO" / "XOP Networks") onto separate lines with
    // <br> — the one line break EVERY email client honors, including Outlook's
    // Word rendering engine. `display:block` on an inline <span> is silently
    // ignored by Outlook-class clients, which collapsed the two lines onto one
    // as "CEOXOP Networks" (no separator) — the reported "no carriage return"
    // bug. Parent <p> already carries the font/size/color, so plain lines joined
    // by <br> render identically where display:block worked, and correctly where
    // it didn't.
    const titleHtml = (speaker.title || '')
      .split(/\r?\n/).map(line => line.trim()).filter(Boolean)
      .join('<br />') || '&nbsp;'

    const objectPos = speaker.objectPosition || '50% 50%'
    // When a photo exists, render an <img> with object-position so the saved
    // crop matches what the editor preview showed. Otherwise fall back to an
    // initials avatar (VML for Outlook, plain div for everything else).
    const photoBlock = photoUrl
      ? `<img src="${photoUrl}" alt="${safeName}" width="120" height="120" style="width:120px;height:120px;display:block;border:0;border-radius:50%;object-fit:cover;object-position:${objectPos};background:#e2e8f0;" />`
      : `<!--[if mso]>
             <v:roundrect xmlns:v="urn:schemas-microsoft-com:vml" xmlns:w="urn:schemas-microsoft-com:office:word" arcsize="50%" stroke="false" fillcolor="${accent}" style="width:120px;height:120px;v-text-anchor:middle;">
               <w:anchorlock/>
               <center style="color:#ffffff;font-family:Arial,Helvetica,sans-serif;font-size:36px;font-weight:bold;letter-spacing:1px;">${initials}</center>
             </v:roundrect>
             <![endif]-->
             <!--[if !mso]><!-->
             <div style="width:120px;height:120px;line-height:120px;background:${accent};color:#ffffff;text-align:center;font-family:Arial,Helvetica,sans-serif;font-size:36px;font-weight:700;border-radius:50%;letter-spacing:1px;">${initials}</div>
             <!--<![endif]-->`

    return `
          <td class="mobile-stack" valign="top" align="center" style="vertical-align:top;padding:12px;">
            <table cellpadding="0" cellspacing="0" border="0" role="presentation" width="180" style="width:180px;">
              <tr>
                <td align="center" style="padding-bottom:14px;">
                  ${photoBlock}
                </td>
              </tr>
              <tr>
                <td align="center" style="padding:0 4px;">
                  <p style="margin:0 0 4px 0;font-family:Arial,Helvetica,sans-serif;font-size:16px;font-weight:700;color:#1a202c;line-height:22px;">${speaker.name || ''}</p>
                  <p style="margin:0;font-family:Arial,Helvetica,sans-serif;font-size:13px;color:#4a5568;line-height:18px;">${titleHtml}</p>
                </td>
              </tr>
            </table>
          </td>
        `
  }

  return `
      <!-- Speakers (inlined inside webinar_details) -->
      <tr>
        <td bgcolor="${speakersBg}" style="background-color:${speakersBg};padding:0;">
          <table width="100%" cellpadding="0" cellspacing="0" border="0" role="presentation" bgcolor="${speakersBg}" style="background-color:${speakersBg};">
            <tr>
              <td class="mobile-pad" align="center" style="padding:32px 40px 4px 40px;text-align:center;">
                <h2 style="margin:0;font-family:Arial,Helvetica,sans-serif;font-size:22px;font-weight:700;color:#1a202c;letter-spacing:0.2px;line-height:28px;">${speakersHeading}</h2>
                <div style="width:48px;height:3px;background:#667eea;margin:14px auto 0 auto;border-radius:2px;font-size:0;line-height:0;">&nbsp;</div>
              </td>
            </tr>
            <tr>
              <td class="mobile-pad" align="center" style="padding:12px 24px 24px 24px;">
                <table cellpadding="0" cellspacing="0" border="0" role="presentation" align="center" style="margin:0 auto;">
                  <tr>
                    ${visibleSpeakers.map((s, i) => renderSpeakerCard(s, i)).join('')}
                  </tr>
                </table>
              </td>
            </tr>
          </table>
        </td>
      </tr>
      `
}
